import { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import { Field, Link, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalContext } from 'src/context/Modal/ModalContext';
import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import { FetchState } from 'src/enums/FetchState';
import { LoginContent } from 'src/enums/loginContent';

import { LoginRequestData } from 'src/services/account/LoginDtos';
import { loginRequest } from 'src/services/account/loginRequest';

import { AuthenticatedUserProfile } from 'src/types/AuthenticatedUserProfile';
import { ContentAccessLevel } from 'src/types/ContentAccessLevel';

import { clearLocalStorage } from 'src/utils/clearLocalStorage';
import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { ForgotPassword } from 'src/components/account/Login/_ForgotPassword/_ForgotPassword';
import { FormErrorBox } from 'src/components/account/Login/_FormErrorBox/_FormErrorBox';
import { SupportLinks } from 'src/components/account/Login/_SupportLinks/_SupportLinks';
import { Button } from 'src/components/basicContent/_Button/Button';
import { ButtonWithLoader } from 'src/components/basicContent/_ButtonWithLoader/ButtonWithLoader';

import { LoginFormFields } from '../LoginFormFields';

import styles from './LoginFields.module.css';

type loginProps = {
	fields: LoginFormFields;
	contentAccessLevel: ContentAccessLevel;
	loginBtnColour?: string;
	redirectUrl?: string;
	disableUrlRedirect?: boolean;
	loginState?: FetchState;
	setLoginState?: (value: FetchState) => void;
	setLoginContent?: (value: number) => void;
};

export default function LoginFields(props: loginProps): JSX.Element {
	const RcnUser = useContext(RcnUserContext);
	const ModalState = useContext(ModalContext);

	const router = useRouter();

	const { register, handleSubmit } = useForm();

	const { loginState, setLoginState } = props;
	const [showPassword, setShowPassword] = useState(false);

	const section = useRef<HTMLElement>(null);

	const [authenticatedUserProfileState, setAuthenticatedUserProfileState] = useState<AuthenticatedUserProfile>();

	const isEditing = IsSitecoreEditing();
	const buttonColour = props.loginBtnColour ? props.loginBtnColour : props.contentAccessLevel.buttonColour;

	async function login(loginFormData: LoginRequestData) {
		setLoginState?.(FetchState.LOADING);

		const { authenticatedUserProfile, state } = await loginRequest(loginFormData);

		setAuthenticatedUserProfileState(authenticatedUserProfile);
		RcnUser.updateAuthenticatedUserProfile(authenticatedUserProfile);
		clearLocalStorage();

		if (state === FetchState.SUCCESS && props.contentAccessLevel.isAllowedAccessToContent && props.disableUrlRedirect) {
			router.push(`${props.redirectUrl}`);
		} else {
			setLoginState?.(state);
		}
	}

	useEffect(() => {
		if (
			loginState === FetchState.SUCCESS &&
			!props.contentAccessLevel.isNonMemberViewingMemberContent &&
			!props.contentAccessLevel.isNonSubscriberViewingSubscriberContent
		) {
			ModalState.updateDisplayLoginFormModal(false);
		}
	}, [
		loginState,
		props,
		props.contentAccessLevel.isNonMemberViewingMemberContent,
		props.contentAccessLevel.isNonSubscriberViewingSubscriberContent,
	]);

	useEffect(() => {
		if (
			loginState === FetchState.SUCCESS && ModalState.modalInfo?.LoginFormModal !== undefined
				? !ModalState.modalInfo.LoginFormModal.Display
				: false
		) {
			RcnUser.updateAuthenticatedUserProfile(authenticatedUserProfileState);
			setLoginState?.(FetchState.START);
		}
	}, [RcnUser, authenticatedUserProfileState, loginState, ModalState.modalInfo?.LoginFormModal, setLoginState]);

	const loginErrorComponent = (errorMessage: Field<string>, errorState: FetchState) => {
		return loginState !== FetchState.LOADING && (loginState === errorState || isEditing) && <FormErrorBox textField={errorMessage} />;
	};

	return (
		<section className="padYS" ref={section}>
			<form onSubmit={handleSubmit(login)} className={styles.content}>
				{!RcnUser.authenticatedUserProfile && (
					<>
						<label className={styles.label}>
							<p>
								<Text field={props.fields.UsernameLabel} className={`${styles.labelText} ${styles.labelRequired}`} tag="strong" />
							</p>

							{props.contentAccessLevel.isRegisteredUserContent && (
								<Text field={props.fields.UsernameSublabel} className={styles.labelText} tag="p" />
							)}

							<input
								{...register('Username')}
								inputMode="email"
								pattern={props.fields.UsernamePattern?.value}
								onInvalid={() => setLoginState?.(FetchState.WARNING)}
								className={`${styles.field} ${styles.fieldBordered}`}
								required
							/>
						</label>
						<label className={styles.label}>
							<p>
								<Text field={props.fields.PasswordLabel} tag="strong" className={`${styles.labelText} ${styles.labelRequired}`} />
							</p>

							<div className={`${styles.field} ${styles.fieldBordered} ${styles.passwordField}`}>
								<input
									{...register('Password')}
									type={!showPassword ? 'password' : 'text'}
									className={`${styles.passwordInput}`}
									required
								/>

								<Button title="Show Password" className={styles.showPassword} onClick={() => setShowPassword(!showPassword)}>
									{!showPassword ? 'Show' : 'Hide'}
								</Button>
							</div>
						</label>
						<ForgotPassword
							headerText={props.fields.ForgotPasswordLabel}
							bodyText={props.fields.ForgotPasswordText}
							targetHash={'#email-secure-link'}
							modalState={ModalState.modalInfo?.LoginFormModal !== undefined ? !ModalState.modalInfo.LoginFormModal.Display : false}
							clickHandler={() => props.setLoginContent?.(LoginContent.SECURE_LOG_IN)}
						/>
					</>
				)}

				<footer className={styles.footer}>
					<div className={styles.buttonWrapper}>
						{props.contentAccessLevel.isNonMemberViewingMemberContent && props.fields.NonMemberLoggedInLink ? (
							<Link field={props.fields.NonMemberLoggedInLink} className={`${buttonColour} ${styles.field}`} />
						) : props.contentAccessLevel.isNonSubscriberViewingSubscriberContent && props.fields.NonSubscriberLoggedInLink ? (
							<Link field={props.fields.NonSubscriberLoggedInLink} className={`${buttonColour} ${styles.field}`} />
						) : (
							<ButtonWithLoader
								loginState={loginState === FetchState.ERROR || loginState === FetchState.WARNING ? FetchState.START : loginState}
								type="submit"
								className={`${buttonColour}`}
							>
								<Text tag="strong" field={props.fields.SubmitText} />
							</ButtonWithLoader>
						)}

						{loginErrorComponent(props.fields.IncorrectCredentialsMessage, FetchState.WARNING)}
						{loginErrorComponent(props.fields.ErrorMessage, FetchState.ERROR)}
					</div>

					{!RcnUser.authenticatedUserProfile && (
						<p>
							<Text field={props.fields.LoginSupportText} />
						</p>
					)}

					{!RcnUser.authenticatedUserProfile && props.fields.LoginSupportLinks && (
						<SupportLinks
							links={props.fields.LoginSupportLinks}
							contentChangeLinks={[
								{
									href: /.rcn.org.uk/,
									action: () => props.setLoginContent?.(LoginContent.SUPPORT_OPTIONS),
								},
							]}
						/>
					)}
				</footer>
			</form>
		</section>
	);
}
